import { apiDelete, apiGet, apiPost, apiPut } from "./api-service";

// Utility functions for checklist API
/**
 * Helper to build API parameters with proper validation
 */
export function buildChecklistParams(params: {
    page?: number;
    limit?: number;
    status?: 'compliant' | 'non_compliant' | 'issues';
    siteId?: string;
    templateId?: string;
    date_from?: string;
    date_to?: string;
    clientId?: string;
}) {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.siteId) queryParams.append('siteId', params.siteId);
    if (params.templateId) queryParams.append('templateId', params.templateId);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.clientId) queryParams.append('clientId', params.clientId);
    
    // Status parameter has known issues in backend
    if (params.status) {
        console.warn('Status filter may not work properly due to backend database schema mismatch');
        queryParams.append('status', params.status);
    }
    
    return queryParams.toString();
}

/**
 * Helper to determine if clientId is required for current user
 */
export function isDSFUser(userType?: string): boolean {
    return userType === 'DSF';
}

// Checklist Template Services
export async function getChecklistTemplates(params?: any) {
    try {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams ? `checklist-template?${queryParams}` : 'checklist-template';
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching checklist templates:', e);
        throw e;
    }
}

export async function getChecklistTemplate(id: string) {
    try {
        console.log('Fetching template details for:', id);
        const response = await apiGet('core', `checklist-templates/${id}`);
        console.log('Raw template API response:', response);
        
        // Handle the response format - check content field first, then message field
        let parsedResponse;
        if (response.content) {
            parsedResponse = response.content;
            console.log('Using template response.content:', parsedResponse);
        } else if (response.message && typeof response.message === 'string') {
            try {
                parsedResponse = JSON.parse(response.message);
                console.log('Parsed template response from message field:', parsedResponse);
            } catch (parseError) {
                console.error('Failed to parse template response.message as JSON:', parseError);
                parsedResponse = response;
            }
        } else {
            parsedResponse = response;
        }
        
        return parsedResponse;
    } catch (e) {
        console.error('Error fetching checklist template:', e);
        throw e;
    }
}

export async function createChecklistTemplate(templateData: any) {
    try {
        const response = await apiPost('core', 'checklist-template', templateData);
        return response;
    } catch (e) {
        console.error('Error creating checklist template:', e);
        throw e;
    }
}

export async function updateChecklistTemplate(id: string, templateData: any) {
    try {
        const response = await apiPut('core', `checklist-template/${id}`, templateData);
        return response;
    } catch (e) {
        console.error('Error updating checklist template:', e);
        throw e;
    }
}

export async function deleteChecklistTemplate(id: string) {
    try {
        const response = await apiDelete('core', `checklist-template/${id}`);
        return response;
    } catch (e) {
        console.error('Error deleting checklist template:', e);
        throw e;
    }
}

export async function customizeChecklistTemplate(masterTemplateId: string, clientId: string, customizations?: any) {
    try {
        const response = await apiPost('core', `checklist-template/${masterTemplateId}/customize`, { 
            clientId, 
            customizations 
        });
        return response;
    } catch (e) {
        console.error('Error customizing checklist template:', e);
        throw e;
    }
}

export async function duplicateChecklistTemplate(templateId: string, newName?: string) {
    try {
        // First, fetch the template to duplicate
        const template = await getChecklistTemplate(templateId);
        
        if (!template) {
            throw new Error('Template not found');
        }
        
        // Create a copy of the template data, excluding fields that should be unique
        const duplicateData = {
            name: newName || `${template.name} (Copy)`,
            description: template.description,
            icon_s3_key: template.icon_s3_key,
            icon_url: template.icon_url,
            industry_ids: template.industry_ids,
            industry_all: template.industry_all,
            appointment_id: template.appointment_id,
            approval_appointment_id: template.approval_appointment_id,
            trigger_type: template.trigger_type,
            frequency_type: template.frequency_type,
            frequency_interval: template.frequency_interval_days || template.frequency_interval,
            conditional_requirement: template.conditional_requirement,
            conditions: template.conditions,
            active: template.active,
            categories: template.categories,
            is_master_template: true // Duplicate is always a master template
        };
        
        // Create the new template
        const response = await createChecklistTemplate(duplicateData);
        return response;
    } catch (e) {
        console.error('Error duplicating checklist template:', e);
        throw e;
    }
}

// Checklist Instance Services
export async function getMyChecklistInstances(filter?: string) {
    try {
        const params = filter ? `?filter=${filter}` : '';
        const response = await apiGet('core', `checklist/my-instances${params}`);
        return response;
    } catch (e) {
        console.error('Error fetching my checklist instances:', e);
        throw e;
    }
}

export async function getChecklistInstance(id: string) {
    try {
        const response = await apiGet('core', `checklist/instance/${id}`);
        return response;
    } catch (e) {
        console.error('Error fetching checklist instance:', e);
        throw e;
    }
}

export async function startChecklistInstance(id: string) {
    try {
        const response = await apiPut('core', `checklist/instance/${id}`, { operation: 'start' });
        return response;
    } catch (e) {
        console.error('Error starting checklist instance:', e);
        throw e;
    }
}

export async function saveChecklistProgress(id: string, data: any) {
    try {
        const response = await apiPut('core', `checklist/instance/${id}`, { 
            operation: 'save_progress',
            data 
        });
        return response;
    } catch (e) {
        console.error('Error saving checklist progress:', e);
        throw e;
    }
}

export async function completeChecklistInstance(id: string, data: any) {
    try {
        const response = await apiPut('core', `checklist/instance/${id}`, { 
            operation: 'complete',
            data 
        });
        return response;
    } catch (e) {
        console.error('Error completing checklist instance:', e);
        throw e;
    }
}

// Dashboard and Reporting Services
export async function getChecklistDashboard(params?: any) {
    try {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams ? `checklist/dashboard?${queryParams}` : 'checklist/dashboard';
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching checklist dashboard:', e);
        throw e;
    }
}

export async function getComplianceStatus(clientId?: string, siteId?: string) {
    try {
        const params = new URLSearchParams();
        if (clientId) params.append('clientId', clientId);
        if (siteId) params.append('siteId', siteId);
        
        const queryString = params.toString();
        const url = queryString ? `checklist-compliance?${queryString}` : 'checklist-compliance';
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching compliance status:', e);
        throw e;
    }
}

export async function triggerChecklistEvaluation() {
    try {
        const response = await apiPost('core', 'checklist-trigger', { action: 'evaluate_all' });
        return response;
    } catch (e) {
        console.error('Error triggering checklist evaluation:', e);
        throw e;
    }
}

// Submission Services
export async function getChecklistSubmissions(params?: any) {
    try {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams ? `checklist/submissions?${queryParams}` : 'checklist/submissions';
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching checklist submissions:', e);
        throw e;
    }
}

export async function getChecklistSubmission(id: string) {
    try {
        const response = await apiGet('core', `checklist/submission/${id}`);
        return response;
    } catch (e) {
        console.error('Error fetching checklist submission:', e);
        throw e;
    }
}

export async function createChecklistSubmission(submissionData: any) {
    try {
        const response = await apiPost('core', 'checklist-submission', submissionData);
        return response;
    } catch (e) {
        console.error('Error creating checklist submission:', e);
        throw e;
    }
}

// Follow-up Tasks Services
export async function getFollowUpTasks(params?: any) {
    try {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams ? `checklist/follow-up-tasks?${queryParams}` : 'checklist/follow-up-tasks';
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching follow-up tasks:', e);
        throw e;
    }
}

export async function updateFollowUpTask(id: string, data: any) {
    try {
        const response = await apiPut('core', `checklist/follow-up-task/${id}`, data);
        return response;
    } catch (e) {
        console.error('Error updating follow-up task:', e);
        throw e;
    }
}

export async function getChecklistCompliance(siteId: string) {
    try {
        const response = await apiGet('core', `checklist-compliance?site_id=${siteId}`);
        return response;
    } catch (e) {
        console.error('Error fetching checklist compliance:', e);
        throw e;
    }
}

export async function getChecklistTasks(params?: any) {
    try {
        const queryParams = new URLSearchParams(params).toString();
        const url = queryParams ? `checklist-submission/tasks?${queryParams}` : 'checklist-submission/tasks';
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching checklist tasks:', e);
        throw e;
    }
}

export async function sendChecklistReminder(params: any) {
    try {
        const response = await apiPost('core', 'checklist/reminder', params);
        return response;
    } catch (e) {
        console.error('Error sending checklist reminder:', e);
        throw e;
    }
}

// Trigger Services (for template testing)
export async function getSitesForTemplate(templateId: string) {
    try {
        const response = await apiGet('core', `checklist-template/${templateId}/eligible-sites`);
        return response;
    } catch (e) {
        console.error('Error fetching sites for template:', e);
        throw e;
    }
}

export async function createTemplateTrigger(triggerData: any) {
    try {
        const response = await apiPost('core', 'checklist-trigger/definitions', triggerData);
        return response;
    } catch (e) {
        console.error('Error creating template trigger:', e);
        throw e;
    }
}

// Completed Checklists Services
/**
 * Fetch completed checklists with pagination and filtering
 * 
 * API Endpoint: GET /checklists-completed
 * 
 * Authentication Requirements:
 * - Client Users: Must have client_id in user profile
 * - DSF Users: Must provide clientId parameter
 * 
 * @param params Optional query parameters
 * @param params.page Page number (default: 1)
 * @param params.limit Items per page (default: 20)
 * @param params.status Status filter - NOTE: Currently broken in backend due to database schema mismatch
 * @param params.siteId Filter by specific site UUID
 * @param params.templateId Filter by specific template UUID
 * @param params.date_from Start date filter (ISO format: YYYY-MM-DD)
 * @param params.date_to End date filter (ISO format: YYYY-MM-DD)
 * @param params.clientId Required for DSF users to specify client
 * 
 * Known Issues:
 * - Status filtering is broken: backend queries 'sub.status' but database uses 'sub.overall_compliance' (boolean)
 * - Some field references in backend may be incorrect (e.g., 'sub.submitted_by' vs 'sub.executor_employee_id')
 * 
 * @returns Promise<ChecklistSubmissionsResponse> Paginated response with items array
 */
export async function getCompletedChecklists(params?: any) {
    try {
        // Build query parameters according to API spec
        const queryParams = new URLSearchParams();
        
        // Pagination parameters (optional with defaults)
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        
        // Filtering parameters (all optional)
        // NOTE: Status parameter is currently broken in backend - it tries to filter on 
        // 'sub.status' but database uses 'sub.overall_compliance' (boolean)
        // We'll pass it through but warn that it won't work properly
        if (params?.status) {
            console.warn('Status filter may not work properly due to backend database schema mismatch');
            queryParams.append('status', params.status);
        }
        
        if (params?.siteId) queryParams.append('siteId', params.siteId);
        if (params?.templateId) queryParams.append('templateId', params.templateId);
        if (params?.date_from) queryParams.append('date_from', params.date_from);
        if (params?.date_to) queryParams.append('date_to', params.date_to);
        
        // For DSF users - clientId parameter is required
        if (params?.clientId) queryParams.append('clientId', params.clientId);
        
        const queryString = queryParams.toString();
        const url = queryString ? `checklists-completed?${queryString}` : 'checklists-completed';
        
        console.log('Fetching completed checklists from:', url);
        const response = await apiGet('core', url);
        console.log('Raw API response:', response);
        
        // Handle the response format - main list API returns data in "content" field
        let parsedResponse;
        if (response.content) {
            parsedResponse = response.content;
            console.log('Using response.content:', parsedResponse);
        } else if (response.message && typeof response.message === 'string') {
            try {
                parsedResponse = JSON.parse(response.message);
                console.log('Parsed response from message field:', parsedResponse);
            } catch (parseError) {
                console.error('Failed to parse response.message as JSON:', parseError);
                parsedResponse = response;
            }
        } else {
            parsedResponse = response;
        }
        
        // Ensure we have the expected structure
        const finalResponse = {
            total: parsedResponse.total || 0,
            page: parsedResponse.page || 1,
            limit: parsedResponse.limit || 20,
            totalPages: parsedResponse.totalPages || 0,
            items: parsedResponse.items || []
        };
        
        console.log('Final processed response:', finalResponse);
        return finalResponse;
        
    } catch (e) {
        console.error('Error fetching completed checklists:', e);
        throw e;
    }
}

/**
 * Fetch detailed information for a specific checklist submission
 * 
 * API Endpoint: GET /checklists-completed/{id}
 * 
 * Authentication Requirements:
 * - Client Users: Must have client_id in user profile
 * - DSF Users: Must provide clientId parameter
 * 
 * @param id Submission UUID
 * @param clientId Required for DSF users to specify client
 * 
 * @returns Promise<ChecklistSubmissionDetail> Detailed submission with items array
 */
export async function getChecklistSubmissionDetail(id: string, clientId?: string) {
    try {
        // For DSF users, clientId parameter is required
        const queryParams = new URLSearchParams();
        if (clientId) queryParams.append('clientId', clientId);
        
        const queryString = queryParams.toString();
        const url = queryString ? `checklists-completed/${id}?${queryString}` : `checklists-completed/${id}`;
        
        console.log('Fetching checklist detail from:', url);
        const response = await apiGet('core', url);
        console.log('Raw detail API response:', response);
        
        // Handle the response format - check content field first, then message field
        let parsedResponse;
        if (response.content) {
            parsedResponse = response.content;
            console.log('Using detail response.content:', parsedResponse);
        } else if (response.message && typeof response.message === 'string') {
            try {
                parsedResponse = JSON.parse(response.message);
                console.log('Parsed detail response from message field:', parsedResponse);
            } catch (parseError) {
                console.error('Failed to parse detail response.message as JSON:', parseError);
                parsedResponse = response;
            }
        } else {
            parsedResponse = response;
        }
        
        return parsedResponse;
    } catch (e) {
        console.error('Error fetching checklist submission detail:', e);
        throw e;
    }
}

/**
 * Download checklist PDF using the proven s3Service pattern
 * @param pdfUrl The PDF URL from checklist submission  
 * @param filename Optional filename for the downloaded file
 */
export async function downloadChecklistPDF(pdfUrl: string, filename?: string) {
    try {
        if (!pdfUrl) {
            throw new Error('PDF URL is not available for this checklist');
        }
        
        console.log('=== Starting PDF Download ===');
        console.log('Original PDF URL:', pdfUrl);
        console.log('Requested filename:', filename);
        
        // Use the same pattern as your working s3Service.downloadFile
        const response = await fetch(pdfUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/pdf,application/octet-stream,*/*'
            }
        });
        
        console.log('Fetch response status:', response.status);
        console.log('Fetch response statusText:', response.statusText);
        console.log('Response content-type:', response.headers.get('content-type'));
        
        if (!response.ok) {
            let errorBody = '';
            try {
                errorBody = await response.text();
                console.log('Error response body (first 200 chars):', errorBody.substring(0, 200));
            } catch (e) {
                console.log('Could not read error response body');
            }
            
            // Check if we got HTML instead of PDF (common development issue)
            if (errorBody.includes('<!DOCTYPE html>') || errorBody.includes('<html')) {
                throw new Error('PDF endpoint returned HTML page instead of PDF. The backend PDF endpoint may not be properly configured.');
            }
            
            throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
        }
        
        // Check content type
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
            throw new Error('PDF endpoint returned HTML page instead of PDF. Check if the backend PDF endpoint is properly implemented.');
        }
        
        // Get the blob - same as your working s3Service.downloadFile
        const blob = await response.blob();
        
        console.log('Downloaded blob - size:', blob.size, 'type:', blob.type);
        
        if (blob.size === 0) {
            throw new Error('Received empty PDF file');
        }
        
        // Create download link - same pattern as s3Service.downloadFile
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename || `checklist-${new Date().getTime()}.pdf`);
        
        console.log('Triggering download with filename:', link.download);
        
        // Append to html link element page
        document.body.appendChild(link);
        
        // Start download
        link.click();
        
        // Clean up and remove the link
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ PDF download completed successfully');
        return { success: true };
        
    } catch (error: any) {
        console.error('❌ PDF download failed:', error);
        console.error('Error details:', {
            message: error.message,
            url: pdfUrl
        });
        throw error;
    }
}

/**
 * Download multiple checklist PDFs (fallback to old API if needed)
 */
export async function downloadMultipleChecklistsPDF(submissions: Array<{id: string, pdfUrl: string | null, name: string}>) {
    try {
        const availablePDFs = submissions.filter(sub => sub.pdfUrl);
        
        if (availablePDFs.length === 0) {
            throw new Error('No PDF files are available for the selected checklists');
        }
        
        // Download each PDF individually
        for (const submission of availablePDFs) {
            const filename = `${submission.name}-${submission.id.substring(0, 8)}.pdf`;
            await downloadChecklistPDF(submission.pdfUrl!, filename);
            
            // Small delay between downloads to prevent browser blocking
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        return { 
            success: true, 
            downloadedCount: availablePDFs.length,
            totalRequested: submissions.length 
        };
    } catch (e) {
        console.error('Error downloading multiple checklists:', e);
        throw e;
    }
}

// Legacy functions for backward compatibility (deprecated)
export async function exportChecklistToPDF(id: string) {
    try {
        console.warn('exportChecklistToPDF is deprecated. Use downloadChecklistPDF with pdfUrl instead.');
        const response = await apiGet('core', `checklist-submission/${id}/export/pdf`);
        return response;
    } catch (e) {
        console.error('Error exporting checklist to PDF:', e);
        throw e;
    }
}

export async function exportMultipleChecklistsToPDF(ids: string[]) {
    try {
        console.warn('exportMultipleChecklistsToPDF is deprecated. Use downloadMultipleChecklistsPDF instead.');
        const response = await apiPost('core', 'checklist-submission/export/pdf', { submission_ids: ids });
        return response;
    } catch (e) {
        console.error('Error exporting multiple checklists to PDF:', e);
        throw e;
    }
}

export async function getNonComplianceActions(params?: any) {
    try {
        const queryParams = new URLSearchParams({
            response_type: 'non_compliant',
            ...params
        }).toString();
        const url = `checklist-submission?${queryParams}`;
        const response = await apiGet('core', url);
        return response;
    } catch (e) {
        console.error('Error fetching non-compliance actions:', e);
        throw e;
    }
}

// Ensure this is treated as a module
export {};
